import React from 'react';
import { Button, LabeledInput, AlertNotification } from "@commonsku/styles";
import {oauth} from '../../utils';

type ShopAggregationStatus = {
  tenant_domain_key: string;
  tenant_name: string;
  shop_number: string | number;
  shop_name?: string;
  in_aggregating: boolean;
  message: string;
};

const ResetShopAggregation = () => {
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [info, setInfo] = React.useState('');
  const [shop, setShop] = React.useState<ShopAggregationStatus | null>(null);

  const handleError = (err: any, fallback: string) => {
    if (err.json?.error && typeof err.json.error === 'string') {
      setError(err.json.error);
    } else if (err.response?.status === 404) {
      setError(fallback);
    } else if (err.response?.status === 403) {
      setError('Access denied. You do not have permission to perform this action.');
    } else if (err.response?.status === 400) {
      setError('Invalid request. Please check your input and try again.');
    } else {
      setError('An error occurred while processing your request. Please try again.');
    }
  };

  const handleCheck = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInfo('');
    setShop(null);

    try {
      const formData = Object.fromEntries(new FormData(e.currentTarget).entries());
      const response = await oauth('POST', 'tech-support', {
        ...formData,
        actionName: 'check_shop_aggregation',
      });
      const data = response.json as ShopAggregationStatus;
      setShop(data);
      if (data.in_aggregating) {
        setInfo(data.message);
      } else {
        setSuccess(data.message);
      }
    } catch (err) {
      handleError(
        err,
        'Shop or tenant not found. Please check the tenant domain key and shop number.'
      );
    }
  };

  const handleReset = async () => {
    if (!shop) {
      return;
    }

    const confirmed = window.confirm(
      `Reset aggregation for shop #${shop.shop_number}?\n\nThis will set in_aggregating to 0.`
    );
    if (!confirmed) {
      return;
    }

    setError('');
    setSuccess('');
    setInfo('');
    try {
      const response = await oauth('POST', 'tech-support', {
        tenant_domain_key: shop.tenant_domain_key,
        shop_number: shop.shop_number,
        actionName: 'reset_shop_aggregation',
      });
      const data = response.json as ShopAggregationStatus;
      setShop(data);
      setSuccess(data.message);
    } catch (err) {
      handleError(
        err,
        'Unable to reset shop aggregation. Please check the details and try again.'
      );
    }
  };

  return (
    <div>
      <form action="" onSubmit={handleCheck}>
        <LabeledInput
          label="Tenant Domain Key"
          placeholder="Enter tenant domain key"
          name="tenant_domain_key"
          type="text"
          required
        />
        <LabeledInput
          label="Shop Number"
          placeholder="Enter shop number"
          name="shop_number"
          type="text"
          required
        />
        <Button type="submit">Check Shop</Button>
      </form>

      {error && (
        <AlertNotification alertType="error" style={{marginTop: '10px'}}>
          {error}
        </AlertNotification>
      )}
      {info && (
        <AlertNotification alertType="warn" style={{marginTop: '10px'}}>
          {info}
        </AlertNotification>
      )}
      {success && (
        <AlertNotification alertType="success" style={{marginTop: '10px'}}>
          {success}
        </AlertNotification>
      )}

      {shop && (
        <div style={{marginTop: '20px'}}>
          <div style={{marginBottom: '10px'}}>
            <strong>Tenant:</strong> {shop.tenant_name} ({shop.tenant_domain_key})
          </div>
          <div style={{marginBottom: '10px'}}>
            <strong>Shop:</strong> #{shop.shop_number}
            {shop.shop_name ? ` — ${shop.shop_name}` : ''}
          </div>
          {shop.in_aggregating && (
            <Button type="button" onClick={handleReset}>
              Reset Aggregation
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

ResetShopAggregation.label = 'Reset Shop Aggregation';

export default ResetShopAggregation;
